/**
 * Raids on the Friends screen (v1 ticket 47), from `GET /api/raids`: beside each member their glory and, for a
 * friend, **Raid** (disabled with one plain reason); then "At your gates" (each army with **Defend**), an army
 * built and not sent (**Continue**), "On their way", and the reports, newest first, the unread marked. Templates
 * only; the raid screens themselves are their own chunk.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { num, t } from '../../i18n';
import { tParts } from '../../bidi-html';
import { go } from '../../router';
import type { RaidOverview } from './raid-api';
import { raidBlock } from './raid-army';
import { aimName } from './raid-bits';
import { entryPath } from './tour-state';

const at = (aim: Parameters<typeof aimName>[0]) => (aim?.slot || aim?.title ? html` <span class="muted small">${aimName(aim)}</span>` : nothing);

const name = (n: string) => html`<bdi dir="auto">${n}</bdi>`;
const glory = (n: number) => html`<span class="rb-glory" title=${t('raid.glory')}><span aria-hidden="true">✦</span> ${num(n)}<span class="sr-only"> ${t('raid.glory')}</span></span>`;

/** What stands beside a member of the circle: their glory, and for a friend the way to raid them (`after` sits at the end of that first line, e.g. the friend's `⋯`). */
export function memberRaid(o: RaidOverview | undefined, member: { id: string; you: boolean }, after: TemplateResult | typeof nothing = nothing) {
  if (!o) return after;
  // Glory and the action sit in two fixed columns, so every row's glory lines up whether or not it has a button.
  if (member.you) return html`${glory(o.glory)}<span class="rb-act" aria-hidden="true"></span>`;
  const f = o.friends.find((x) => x.id === member.id);
  if (!f) return after;
  const block = raidBlock(f.raid, o.launchesLeft);
  return html`${glory(f.glory)}
    <span class="rb-act"><button class="btn primary rb-raid" ?disabled=${!!block} @click=${() => go(entryPath(o, `/raid/new/${encodeURIComponent(f.id)}`))}>${t('raid.raid')}</button>${after}</span>
    ${block ? html`<span class="rb-why muted small">${t(`raid.block.${block}`)}</span>` : nothing}`;
}

/** The armies and reports, under the members. */
export function raidBoard(o: RaidOverview | undefined) {
  if (!o) return nothing;
  const open = (id: string) => go(`/raid/${encodeURIComponent(id)}`);
  const weapons = (n: number) => (n === 1 ? t('raid.weaponsOne') : t('raid.weapons', { n }));
  return html`
    ${o.incoming.length
      ? html`<div class="card rb-incoming">
          <h3>${t('raid.atGates')}</h3>
          <ul class="rb-list">
            ${o.incoming.map((r) => html`<li class="rb-row">
              <span class="rb-text">${tParts('raid.armyOf', { name: name(r.from.name) })}${at(r.district)} <span class="muted small">${weapons(r.weapons)}</span></span>
              <button class="btn primary rb-defend" @click=${() => go(`/raid/${encodeURIComponent(r.id)}`)}>${t('raid.defend')}</button>
            </li>`)}
          </ul>
        </div>`
      : nothing}
    ${o.draft || o.outgoing.length
      ? html`<div class="card rb-outgoing">
          <h3>${t('raid.onTheirWay')}</h3>
          <ul class="rb-list">
            ${o.draft
              ? html`<li class="rb-row rb-draft">
                  <span class="rb-text">${tParts('raid.draft', { name: name(o.draft.to.name) })}${at(o.draft.district)}</span>
                  <button class="btn primary rb-continue" @click=${() => open(o.draft!.id)}>${t('raid.continue')}</button>
                </li>`
              : nothing}
            ${o.outgoing.map((r) => html`<li class="rb-row">
              <button class="rb-open" @click=${() => open(r.id)}>
                <span class="rb-text">${tParts('raid.armyTo', { name: name(r.to.name) })}${at(r.district)} <span class="muted small">${weapons(r.weapons)}</span></span>
              </button>
            </li>`)}
          </ul>
        </div>`
      : nothing}
    ${o.reports.length
      ? html`<div class="card rb-reports">
          <h3>${t('raid.reports')}</h3>
          <ul class="rb-list">
            ${o.reports.map((r) => html`<li class="rb-row ${r.unread ? 'is-unread' : ''}">
              <button class="rb-open" @click=${() => open(r.id)}>
                <span class="rb-text">
                  ${tParts(r.side === 'attacker' ? 'raid.reportOn' : 'raid.reportFrom', { name: name(r.other.name) })}${at(r.district)}
                  <span class="muted small">${t(`raid.band.${r.band}`)}</span>
                </span>
                ${r.unread ? html`<span class="pill accent rb-unread">${t('raid.unread')}</span>` : nothing}
                ${glory(r.glory)}
              </button>
            </li>`)}
          </ul>
        </div>`
      : nothing}`;
}
